import { CIR_PERMISSION_TABS, EAccessLevel, EPermissionTab, IPermissionMatrix } from 'entities/Permission/Permission.models';
import { EUserProfile, EUserRole, IUserModel } from 'entities/User/User.models';

// FE nav-gating only — the BE module-guard is the real enforcement. A missing tab in the matrix
// means NoAccess. superadmin is wildcard (handled by callers via `isSuperAdmin`).
export const hasAccess = (
  permissions: IPermissionMatrix | null | undefined,
  tab: EPermissionTab,
  min: EAccessLevel = EAccessLevel.ViewOnly
): boolean => {
  const granted = permissions?.[tab] ?? EAccessLevel.NoAccess;

  return granted >= min;
};

// MBC-84 Phase 2: whether the effective permissions grant at least one CIR module.
// NB: NO "no permissions → true" fallback here — a profile-less franchisor keeps the legacy MBGo
// wildcard but never gets CIR; only an explicit CIR grant from the BE matrix opens the portal.
export const hasAnyCirAccess = (permissions: IPermissionMatrix | null | undefined): boolean =>
  !!permissions && CIR_PERMISSION_TABS.some((tab) => hasAccess(permissions, tab));

export const isSuperAdminRole = (role?: string): boolean => role === (EUserRole.SuperAdmin as string);

// A franchisor user is the only role the permission overlay narrows. Every other role keeps its
// existing role-based access untouched (franchisee/salesop/manager/customer).
export const isFranchisorRole = (role?: string): boolean => role === EUserRole.Franchisor;

export interface IAuthUserRestriction {
  role?: EUserRole;
  // Omit to ignore profile entirely; pass `null` to match a user with no profile assigned.
  profile?: EUserProfile | null;
}

// Generic content-gating matcher: true if authUser matches ANY of the given (role, profile)
// restrictions. Add more entries at the call site as new roles/profiles need gating — no new
// per-case helper required.
export const matchesAnyRestriction = (
  authUser: Pick<IUserModel, 'role' | 'profile'> | null | undefined,
  restrictions: IAuthUserRestriction[]
): boolean =>
  restrictions.some(
    ({ role, profile }) =>
      (role === undefined || authUser?.role === role) &&
      (profile === undefined || (authUser?.profile ?? null) === profile)
  );

// Single source of truth for the "marketing-profile franchisor is read-only" content gate.
// Referenced by isContentRestrictedForUser/useIsContentRestricted below — extend this list (not
// the call sites) when another role/profile combo needs the same read-only treatment.
export const READ_ONLY_CONTENT_RESTRICTIONS: IAuthUserRestriction[] = [
  { role: EUserRole.Franchisor, profile: EUserProfile.Marketing },
];

export const isContentRestrictedForUser = (authUser: Pick<IUserModel, 'role' | 'profile'> | null | undefined): boolean =>
  matchesAnyRestriction(authUser, READ_ONLY_CONTENT_RESTRICTIONS);
